// "People (sample)" section used inside the drill panel.
//
// Shows a small sample of employees that match a given KPI scope and exposes
// a "View all →" affordance that expands the same drawer into a scrollable
// full list — no navigation, the drawer body just scrolls.
//
// Visual reference: the second screenshot supplied by design (Active
// Headcount drawer with avatar circles, name, designation · location, and
// tenure on the right).

import React, { useMemo } from 'react';
import {
  Avatar,
  Box,
  ButtonBase,
  CircularProgress,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import type {
  HrEmployeeStatus,
  HrFilterMap,
  HrStatsPersonRow,
  HrStatsPeopleScope,
} from '../../types/hrStats';
import { useHrPeople } from '../../hooks/useHrPeople';
import { CHART_PALETTE } from './primitives/peopleInsightsTokens';

const SAMPLE_SIZE = 6;
const FULL_LIMIT = 500;

export interface PeopleListSectionProps {
  scope: HrStatsPeopleScope;
  pageFilters: HrFilterMap;
  employeeStatus: HrEmployeeStatus;
  /** Title shown above the list. Defaults to "People". */
  title?: string;
  /** Whether the parent has expanded the section to "view all" mode. */
  expanded: boolean;
  /** Toggle expanded mode. */
  onToggleExpanded: () => void;
  /** When the panel is collapsed/closed we don't fetch. */
  enabled?: boolean;
}

const PeopleListSection: React.FC<PeopleListSectionProps> = ({
  scope,
  pageFilters,
  employeeStatus,
  title = 'People',
  expanded,
  onToggleExpanded,
  enabled = true,
}) => {
  const limit = expanded ? FULL_LIMIT : SAMPLE_SIZE;
  const peopleQuery = useHrPeople({
    scope,
    filters: pageFilters,
    employeeStatus,
    limit,
    enabled,
  });

  const total = peopleQuery.data?.total ?? 0;
  const rows = peopleQuery.data?.rows ?? [];

  // When expanded, we want a scrollable region; when sampled we let the
  // container flow naturally.
  const listSx = expanded
    ? {
        maxHeight: 'calc(100vh - 320px)',
        overflowY: 'auto',
        pr: 0.5,
      }
    : undefined;

  const showViewAll = !expanded && total > rows.length;

  return (
    <Stack spacing={1.25} sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: 'text.primary' }}
        >
          {title}
          
          {expanded && (
            <Typography
              component="span"
              variant="caption"
              sx={{ color: 'text.secondary', fontWeight: 500, ml: 0.75 }}
            >
              ({total})
            </Typography>
          )}
        </Typography>

        {showViewAll && (
          <ViewAllButton
            label={`View all ${total}`}
            onClick={onToggleExpanded}
          />
        )}
        {expanded && total > SAMPLE_SIZE && (
          <ViewAllButton label="Show less" onClick={onToggleExpanded} />
        )}
      </Stack>

      {peopleQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={20} />
        </Box>
      ) : peopleQuery.isError ? (
        <Typography variant="body2" sx={{ color: 'error.main' }}>
          Couldn’t load people for this view.
        </Typography>
      ) : rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
          No employees match this view.
        </Typography>
      ) : (
        <Stack spacing={0.75} sx={listSx}>
          {rows.map((person, idx) => (
            <PersonRow key={person.email ?? `${person.full_name}-${idx}`} person={person} colorIdx={idx} />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

// ---------------------------------------------------------------------------

interface ViewAllButtonProps {
  label: string;
  onClick: () => void;
}

const ViewAllButton: React.FC<ViewAllButtonProps> = ({ label, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.5,
      color: '#4F46E5',
      fontWeight: 600,
      fontSize: '0.8125rem',
      borderRadius: 1,
      px: 0.5,
      py: 0.25,
      '&:hover': { textDecoration: 'underline' },
    }}
  >
    {label}
    {label.startsWith('View') && <ArrowForwardIcon sx={{ fontSize: 14 }} />}
  </ButtonBase>
);

interface PersonRowProps {
  person: HrStatsPersonRow;
  colorIdx: number;
}

const PersonRow: React.FC<PersonRowProps> = ({ person, colorIdx }) => {
  const initials = useMemo(() => buildInitials(person.full_name), [person.full_name]);

  // Pastel-tinted avatar background derived from the chart palette so a list
  // of people looks lively without being noisy.
  const baseColor = CHART_PALETTE[colorIdx % CHART_PALETTE.length];
  const avatarBg = alpha(baseColor, 0.16);
  const avatarFg = baseColor;

  const subline =
    [person.designation, person.location].filter(Boolean).join(' · ') || '—';
  const tenureLabel =
    person.tenure_years != null ? `${person.tenure_years.toFixed(1)} yrs` : null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 1.25,
        py: 1,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#fff',
      }}
    >
      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: '0.75rem',
          fontWeight: 700,
          backgroundColor: avatarBg,
          color: avatarFg,
        }}
      >
        {initials}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {person.full_name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {subline}
        </Typography>
      </Box>
      {tenureLabel && (
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
          {tenureLabel}
        </Typography>
      )}
    </Stack>
  );
};

function buildInitials(fullName: string): string {
  const trimmed = (fullName || '').trim();
  if (!trimmed) return '—';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default PeopleListSection;
